import { FC } from 'react';
import styled from 'styled-components';

import { useI18nValues } from '@/entities/vfo/hooks/useI18nValues';
import { ActivationStatus, Member } from '@/entities/vfo/types';
import { COLORS } from '@/shared/constants/colors';
import { Icon } from '@/shared/uikit/Icon';
import { useTranslation } from '@/shared/utils/i18n/useTranslation';
import { useModalsStore } from '@/widgets/modals';

import { Avatar } from './Avatar';

type UserCellProps = {
  member: Member;
  small?: boolean;
  showStatus?: boolean;
  hideAvatar?: boolean;
  clickable?: boolean;
  onOpenCase?: () => void;
};

export const UserCell: FC<UserCellProps> = ({
  member,
  small,
  hideAvatar,
  showStatus,
  onOpenCase,
  clickable = true,
}) => {
  const { t } = useTranslation('features/vfo', {
    keyPrefix: 'membersList',
  });

  const { tag: tagI18n } = useI18nValues();
  const modals = useModalsStore();

  const { firstName, lastName, tag, isFounder } = member;
  const initials = firstName && lastName ? `${firstName[0]}${lastName[0]}` : null;
  const showName = initials && member.activationStatus === ActivationStatus.ACTIVE;
  return (
    <ClickableContainer
      onClick={
        clickable
          ? () => {
              modals.openModal({
                type: 'VfoProfileModal',
                initialMember: member,
                onOpenCase,
              });
            }
          : undefined
      }
    >
      {!hideAvatar && <Avatar member={member} showStatus={showStatus} />}
      <UserWrapper>
        <Name style={{ fontSize: small ? 12 : 14 }}>
          {showName ? `${firstName} ${lastName}` : t('invitedMember')}
        </Name>
        {isFounder ? (
          <Tag>
            <Icon name="medal" size="s12" />
            <span>{t('founder')}</span>
          </Tag>
        ) : (
          tag && <Tag>{tagI18n(tag)}</Tag>
        )}
      </UserWrapper>
    </ClickableContainer>
  );
};

const ClickableContainer = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 12px;

  &:hover {
    opacity: 0.75;
  }
`;

const UserWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: start;
  gap: 2px;
`;

const Name = styled.span`
  color: ${COLORS.systemDark300};
  line-height: 20px;
`;

const Tag = styled.span`
  font-size: 12px;
  color: ${COLORS.systemDark200};
  line-height: 16px;
  display: flex;
  align-items: center;
  gap: 2px;
`;
